import React, { useState } from 'react'
import { useParams, Link } from '@tanstack/react-router'
import { useEmployee, useDirectReports } from '@/hooks/useWorkforce'
import { useUpdateEmployeeStatus } from '@/hooks/useWorkforceMutations'
import { useSalaryAccess } from '@/hooks/useSalaryAccess'
import { withPageProtection } from '@/lib/rbac/hoc'
import { PAGES } from '@/lib/rbac/constants'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { EmptyState } from '@/components/ui/EmptyState'
import { UpdateEmployeeStatusModal } from '@/components/workforce/UpdateEmployeeStatusModal'
import { EmployeePictureUpload } from '@/components/workforce/EmployeePictureUpload'
import {
  ArrowLeft,
  Mail,
  Phone,
  Calendar,
  Building2,
  Briefcase,
  User,
  Users,
  Edit,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  MapPin,
  FileText,
  DollarSign,
  RefreshCw
} from 'lucide-react'

function EmployeeDetailsContent() {
  const { employeeId } = useParams({ from: '/_auth/workforce/employees/$employeeId/' })
  const { employee, loading, error } = useEmployee(employeeId)
  const { reports, loading: reportsLoading } = useDirectReports(employeeId, { limit: 100 })
  const { canViewSalaries, formatSalaryValue } = useSalaryAccess()
  const updateStatusMutation = useUpdateEmployeeStatus()
  const [showStatusModal, setShowStatusModal] = useState(false)

  const getStatusIcon = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'ACTIVE':
        return <CheckCircle className="w-5 h-5 text-success" />
      case 'INACTIVE':
        return <XCircle className="w-5 h-5 text-brand-gray" />
      case 'PENDING':
        return <Clock className="w-5 h-5 text-warning" />
      case 'TERMINATED':
        return <AlertTriangle className="w-5 h-5 text-error" />
      default:
        return <Clock className="w-5 h-5 text-brand-gray" />
    }
  }

  const getStatusColor = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'ACTIVE':
        return 'bg-success/20 text-success'
      case 'INACTIVE':
        return 'bg-brand-gray/20 text-brand-gray'
      case 'PENDING':
        return 'bg-warning/20 text-warning'
      case 'TERMINATED':
        return 'bg-error/20 text-error'
      default:
        return 'bg-brand-gray/20 text-brand-gray'
    }
  }

  const formatDate = (date: Date | string | null | undefined) => {
    if (!date) return 'N/A'
    try {
      const dateObj = new Date(date)
      if (isNaN(dateObj.getTime())) return 'N/A'
      return new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      }).format(dateObj)
    } catch {
      return 'N/A'
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error || !employee) {
    return (
      <EmptyState
        icon={User}
        title="Colaborador não encontrado"
        description={error || 'Não foi possível carregar os dados do colaborador.'}
        action={
          <Link to={"/workforce/employees" as any}>
            <Button>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar para Lista
            </Button>
          </Link>
        }
      />
    )
  }

  const name = employee.personalInfo?.name || 'Nome não informado'
  const nameParts = name.split(' ').filter(p => p.length > 0)
  const initials = nameParts.length >= 2 ?
    `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}` :
    nameParts.length === 1 ?
    `${nameParts[0][0]}${nameParts[0][1] || 'N'}` :
    'NA'

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to={"/workforce/employees" as any}>
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4" />
            </Button>
          </Link>
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold text-white">
              {name}
            </h1>
            <p className="font-body text-brand-gray mt-1">
              {employee.email}
            </p>
          </div>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowStatusModal(true)}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Alterar Status
          </Button>
          <Link to={`/workforce/employees/${employeeId}/edit` as any}>
            <Button size="sm">
              <Edit className="w-4 h-4 mr-2" />
              Editar
            </Button>
          </Link>
        </div>
      </div>

      {/* Profile Card */}
      <Card className="p-6">
        <div className="flex items-start gap-6">
          <div className="flex-shrink-0">
            <EmployeePictureUpload
              employeeId={employeeId}
              currentPictureUrl={employee.personalInfo?.pictureUrl}
              employeeName={name}
            />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-4">
              <h2 className="font-display text-2xl font-semibold text-white">
                {name}
              </h2>
              {employee.employment?.status && (
                <Badge className={getStatusColor(employee.employment.status)}>
                  <div className="flex items-center gap-1.5">
                    {getStatusIcon(employee.employment.status)}
                    <span className="capitalize">{employee.employment.status.toLowerCase()}</span>
                  </div>
                </Badge>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-brand-gray">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                <span>{employee.email || 'N/A'}</span>
              </div>
              {employee.personalInfo?.phone && (
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  <span>{employee.personalInfo.phone}</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                <span>{employee.employment?.department?.name || 'N/A'}</span>
              </div>
              <div className="flex items-center gap-2">
                <Briefcase className="w-4 h-4" />
                <span>{employee.employment?.role?.name || 'N/A'}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>Admissão: {formatDate(employee.employment?.startDate)}</span>
              </div>
              {canViewSalaries && employee.employment?.salary && (
                <div className="flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />
                  <span>{formatSalaryValue(employee.employment.salary)}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* Employment Details */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6">
          <h3 className="font-display text-xl font-semibold text-white mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Informações de Emprego
          </h3>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-brand-gray">Status:</span>
              <span className="text-white font-medium capitalize">
                {employee.employment?.status?.toLowerCase() || 'N/A'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-brand-gray">Data de Admissão:</span>
              <span className="text-white font-medium">
                {formatDate(employee.employment?.startDate)}
              </span>
            </div>
            {employee.employment?.endDate && (
              <div className="flex justify-between">
                <span className="text-brand-gray">Data de Saída:</span>
                <span className="text-white font-medium">
                  {formatDate(employee.employment.endDate)}
                </span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-brand-gray">Tipo de Contrato:</span>
              <span className="text-white font-medium">
                {employee.employment?.contractType || 'N/A'}
              </span>
            </div>
            {canViewSalaries && (
              <>
                <div className="flex justify-between">
                  <span className="text-brand-gray">Salário:</span>
                  <span className="text-white font-medium">
                    {formatSalaryValue(employee.employment?.salary)}
                  </span>
                </div>
              </>
            )}
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="font-display text-xl font-semibold text-white mb-4 flex items-center gap-2">
            <User className="w-5 h-5" />
            Informações Pessoais
          </h3>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-brand-gray">Nome Completo:</span>
              <span className="text-white font-medium">
                {employee.personalInfo?.name || 'N/A'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-brand-gray">Email:</span>
              <span className="text-white font-medium">{employee.email}</span>
            </div>
            {employee.personalInfo?.phone && (
              <div className="flex justify-between">
                <span className="text-brand-gray">Telefone:</span>
                <span className="text-white font-medium">
                  {employee.personalInfo.phone}
                </span>
              </div>
            )}
            {employee.personalInfo?.address && (
              <div className="flex justify-between">
                <span className="text-brand-gray">Endereço:</span>
                <span className="text-white font-medium">
                  {[
                    employee.personalInfo.address.street,
                    employee.personalInfo.address.city,
                    employee.personalInfo.address.state,
                    employee.personalInfo.address.zipCode
                  ].filter(Boolean).join(', ') || 'N/A'}
                </span>
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Direct Reports */}
      {reports && reports.length > 0 && (
        <Card className="p-6">
          <h3 className="font-display text-xl font-semibold text-white mb-4 flex items-center gap-2">
            <Users className="w-5 h-5" />
            Subordinados Diretos ({reports.length})
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {reports.map((report) => {
              const reportName = report.personalInfo?.name || 'Nome não informado'
              const reportNameParts = reportName.split(' ').filter(p => p.length > 0)
              const reportInitials = reportNameParts.length >= 2 ?
                `${reportNameParts[0][0]}${reportNameParts[reportNameParts.length - 1][0]}` :
                reportNameParts.length === 1 ?
                `${reportNameParts[0][0]}${reportNameParts[0][1] || 'N'}` :
                'NA'

              return (
                <Link
                  key={report.employeeId}
                  to={`/workforce/employees/${report.employeeId}` as any}
                  className="block"
                >
                  <Card className="p-4 card-hover">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-brand-primary to-brand-accent-600 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-bold text-brand-dark">
                          {reportInitials.toUpperCase()}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-white truncate">{reportName}</p>
                        <p className="text-xs text-brand-gray truncate">
                          {report.employment?.role?.name || 'N/A'}
                        </p>
                      </div>
                    </div>
                  </Card>
                </Link>
              )
            })}
          </div>
        </Card>
      )}

      {/* Status Update Modal */}
      {showStatusModal && employee && (
        <UpdateEmployeeStatusModal
          employee={employee}
          onConfirm={async (status, endDate, reason) => {
            await updateStatusMutation.mutateAsync({
              employeeId,
              status,
              endDate,
              reason
            })
            setShowStatusModal(false)
          }}
          onCancel={() => setShowStatusModal(false)}
        />
      )}
    </div>
  )
}

export default withPageProtection(EmployeeDetailsContent, PAGES.WORKFORCE_EMPLOYEES)
